import { useState, useEffect } from 'react';

function CasesList({ token }) {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCase, setSelectedCase] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchCases();
  }, []);

  const fetchCases = async () => {
    try {
      const [casesResp, statsResp] = await Promise.all([
        fetch('/api/cases', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/stats', { headers: { Authorization: `Bearer ${token}` } })
      ]);
      const casesData = await casesResp.json();
      const statsData = await statsResp.json();
      setCases(casesData);
      setStats(statsData);
    } catch (err) {
      console.error('Error cargando casos:', err);
    } finally {
      setLoading(false);
    }
  };

  const deleteCase = async (id) => {
    if (!window.confirm('Eliminar este caso?')) return;
    try {
      await fetch(`/api/cases/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setCases(cases.filter(c => c.id !== id));
      if (selectedCase?.id === id) setSelectedCase(null);
    } catch (err) {
      console.error('Error eliminando caso:', err);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="cases-page"><div className="spinner" style={{ margin: '3rem auto' }}></div></div>;
  }

  return (
    <div className="cases-page">
      <h2 className="page-title">Casos de Soporte</h2>
      <p className="page-subtitle">Historial de casos registrados por voz</p>

      {/* Estadisticas */}
      {stats && (
        <div className="stats-bar">
          <div className="stat-item">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Total</span>
          </div>
          <div className="stat-item stat-completed">
            <span className="stat-value">{stats.completed}</span>
            <span className="stat-label">Completados</span>
          </div>
          <div className="stat-item stat-pending">
            <span className="stat-value">{stats.pending}</span>
            <span className="stat-label">Pendientes</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{stats.totalCategories}</span>
            <span className="stat-label">Categorias</span>
          </div>
        </div>
      )}

      {cases.length === 0 ? (
        <div className="empty-state">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
          </svg>
          <p>No hay casos registrados aun</p>
        </div>
      ) : (
        <div className="cases-layout">
          <div className="cases-list">
            {cases.map(c => (
              <div key={c.id} className={`case-card ${selectedCase?.id === c.id ? 'selected' : ''} ${c.status === 'completed' ? 'completed' : 'pending'}`}
                onClick={() => setSelectedCase(c)}>
                <div className="case-card-header">
                  <span className={`status-badge status-${c.status}`}>
                    {c.status === 'completed' ? 'Completado' : 'Pendiente'}
                  </span>
                  <button className="case-delete-btn" onClick={(e) => { e.stopPropagation(); deleteCase(c.id); }} title="Eliminar">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                  </button>
                </div>
                <div className="case-category">{c.category}</div>
                <p className="case-summary">{c.problemSummary?.slice(0, 100)}...</p>
                <div className="case-meta">
                  <span>{formatDate(c.createdAt)}</span>
                  <span className={`confidence-badge confidence-${c.confidence}`}>{c.confidence}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Detalle */}
          {selectedCase && (
            <div className="case-detail">
              <div className="detail-header">
                <h3>Detalle del caso</h3>
                <span className={`status-badge status-${selectedCase.status}`}>
                  {selectedCase.status === 'completed' ? 'Completado' : 'Pendiente'}
                </span>
              </div>

              <div className="detail-section">
                <label>Categoria</label>
                <div className="category-badge">{selectedCase.category}</div>
              </div>

              <div className="detail-section">
                <label>Transcripcion del tecnico (problema)</label>
                <p className="transcript-text">"{selectedCase.problemTranscript}"</p>
              </div>

              <div className="detail-section">
                <label>Problema estandarizado</label>
                <p>{selectedCase.problemSummary}</p>
              </div>

              <div className="detail-section">
                <label>Sintomas detectados</label>
                <div className="tags-list">
                  {(selectedCase.matchedSymptoms || []).map((s, i) => (
                    <span key={i} className="tag symptom-tag">{s}</span>
                  ))}
                </div>
              </div>

              <div className="detail-section">
                <label>Solucion sugerida</label>
                <p>{selectedCase.suggestedSolution}</p>
              </div>

              {selectedCase.status === 'completed' && (
                <>
                  <hr className="detail-divider" />

                  <div className="detail-section">
                    <label>Transcripcion del tecnico (solucion)</label>
                    <p className="transcript-text">"{selectedCase.solutionTranscript}"</p>
                  </div>

                  <div className="detail-section">
                    <label>Solucion aplicada (estandarizada)</label>
                    <p className="solution-text">{selectedCase.appliedSolution}</p>
                  </div>

                  {selectedCase.solutionSteps?.length > 0 && (
                    <div className="detail-section">
                      <label>Pasos realizados</label>
                      <ol className="steps-list">
                        {selectedCase.solutionSteps.map((step, i) => <li key={i}>{step}</li>)}
                      </ol>
                    </div>
                  )}

                  {selectedCase.partsUsed?.length > 0 && (
                    <div className="detail-section">
                      <label>Componentes utilizados</label>
                      <div className="tags-list">
                        {selectedCase.partsUsed.map((p, i) => <span key={i} className="tag part-tag">{p}</span>)}
                      </div>
                    </div>
                  )}

                  {selectedCase.observations && (
                    <div className="detail-section">
                      <label>Observaciones</label>
                      <p>{selectedCase.observations}</p>
                    </div>
                  )}
                </>
              )}

              <div className="detail-section detail-meta">
                <span>Creado: {formatDate(selectedCase.createdAt)}</span>
                {selectedCase.completedAt && <span>Completado: {formatDate(selectedCase.completedAt)}</span>}
                <span>Tecnico: {selectedCase.technician}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CasesList;
